import React, { useState } from "react";
import Select from "react-select";
import { X, PackagePlus, AlertTriangle, Save } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch } from "react-redux";
import apiService from "../../../../core/services/apiService";
import { customSelectStyles } from "../../../../core/utils/fieldStyles";
import {
  inventoryUnitOptions,
  inventoyCategoryOptions,
} from "../../../../core/constants/choices";

export default function InventoryModal({
  onClose,
  existingItem = null,
  onUpdated,
}) {
  const dispatch = useDispatch();
  const isEdit = Boolean(existingItem); // ✅ detect edit mode

  const [form, setForm] = useState({
    name: existingItem?.name || "",
    category: existingItem?.category || "",
    unit: existingItem?.unit || "",
    reorderLevel: existingItem?.reorderLevel || "",
    expiryDate: existingItem?.expiryDate
      ? new Date(existingItem.expiryDate).toISOString().split("T")[0]
      : "",
    supplier: existingItem?.supplier || "",
    batchNumber: existingItem?.batchNumber || "",
    notes: existingItem?.notes || "",
    initialStock: existingItem?.quantity || "", // ✅ for edit, show current qty
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // ✅ Update title dynamically
  const title = isEdit ? "Edit Inventory Item" : "Add New Inventory Item";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, option) => {
    setForm((prev) => ({ ...prev, [name]: option ? option.value : "" }));
  };

  const validate = () => {
    if (!form.name.trim()) return "Item name is required.";
    if (!form.unit.trim()) return "Unit is required.";
    if (!form.initialStock || Number(form.initialStock) < 0)
      return "Initial stock must be 0 or more.";
    return "";
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        quantity: Number(form.initialStock),
      };

      if (isEdit) {
        // ✅ Update existing
        await apiService.put(dispatch, `inventory`, existingItem._id, payload);
      } else {
        // ✅ Create new
        await apiService.post(dispatch, "inventory", payload);
      }

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onClose();
        onUpdated?.(); // ✅ reload list or details after success
      }, 1000);
    } catch (err) {
      console.error("Failed to create inventory:", err);
      setError("Failed to save. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0 }}
        className="bg-white rounded-2xl shadow-2xl w-[95%] max-w-3xl p-6 relative overflow-y-auto max-h-[95vh]"
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-4 border-b pb-2">
          <h2 className="text-2xl font-bold flex items-center gap-2 text-gray-800">
            <PackagePlus className="text-blue-600" />
            {title}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-900"
          >
            <X size={24} />
          </button>
        </div>

        {/* Alerts */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="mb-4 flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 rounded-md p-2 text-sm"
            >
              <AlertTriangle size={16} />
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        {success && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="mb-4 bg-green-50 text-green-700 border border-green-200 rounded-md p-2 text-sm text-center"
          >
            Inventory item created successfully!
          </motion.div>
        )}

        {/* Form */}
        <div className="grid grid-cols-2 gap-4">
          {/* Name */}
          <div>
            <label className="text-sm font-medium text-gray-700">Name *</label>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none"
              placeholder="e.g. Paracetamol 500mg"
            />
          </div>

          {/* Category */}
          <div>
            <label className="text-sm font-medium text-gray-700">
              Category
            </label>
            <Select
              options={inventoyCategoryOptions}
              value={
                inventoyCategoryOptions.find(
                  (opt) => opt.value === form.category
                ) || null
              }
              onChange={(option) => handleSelectChange("category", option)}
              styles={customSelectStyles}
              placeholder="Select category"
              isClearable
            />
          </div>

          {/* Unit */}
          <div>
            <label className="text-sm font-medium text-gray-700">Unit *</label>
            <Select
              options={inventoryUnitOptions}
              value={
                inventoryUnitOptions.find((opt) => opt.value === form.unit) ||
                null
              }
              onChange={(option) => handleSelectChange("unit", option)}
              styles={customSelectStyles}
              placeholder="Select unit"
              isClearable
            />
          </div>

          {/* Reorder Level */}
          <div>
            <label className="text-sm font-medium text-gray-700">
              Reorder Level
            </label>
            <input
              type="number"
              name="reorderLevel"
              value={form.reorderLevel}
              onChange={handleChange}
              className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none"
              placeholder="e.g. 10"
            />
          </div>

          {/* Expiry Date */}
          <div>
            <label className="text-sm font-medium text-gray-700">
              Expiry Date
            </label>
            <input
              type="date"
              name="expiryDate"
              value={form.expiryDate}
              onChange={handleChange}
              className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none"
            />
          </div>

          {/* Supplier */}
          <div>
            <label className="text-sm font-medium text-gray-700">
              Supplier
            </label>
            <input
              name="supplier"
              value={form.supplier}
              onChange={handleChange}
              className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none"
              placeholder="Supplier name"
            />
          </div>

          {/* Batch Number */}
          <div>
            <label className="text-sm font-medium text-gray-700">
              Batch Number
            </label>
            <input
              name="batchNumber"
              value={form.batchNumber}
              onChange={handleChange}
              className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none"
              placeholder="e.g. BATCH-2025-01"
            />
          </div>

          {/* Initial Stock */}
          <div>
            <label className="text-sm font-medium text-gray-700">
              Initial Stock *
            </label>
            <input
              type="number"
              name="initialStock"
              value={form.initialStock}
              onChange={handleChange}
              disabled={isEdit}
              className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none"
              placeholder="e.g. 100"
            />
            {isEdit && (
              <p className="text-xs text-gray-500 mt-1">
                Initial stock can only be set when creating a new item.
              </p>
            )}
          </div>

          {/* Notes */}
          <div className="col-span-2">
            <label className="text-sm font-medium text-gray-700">Notes</label>
            <textarea
              name="notes"
              rows={3}
              value={form.notes}
              onChange={handleChange}
              className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none"
              placeholder="Optional notes about the item..."
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-4 mt-6 border-t pt-3">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-5 py-2 rounded-lg text-gray-700 hover:bg-gray-100"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={isSaving}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg text-white ${
              isSaving
                ? "bg-blue-300 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            <Save size={18} />
            {isSaving ? "Saving..." : isEdit ? "Update Item" : "Save Item"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
